export const training = {
  title: 'Entrenamiento Conductual',
  subtitle: 'Aprende a leer e interpretar perfiles conductuales',
  
  modules: {
    foundations: {
      title: 'Fundamentos',
      description: 'Aprenda los conceptos básicos de lectura de perfiles'
    },
    framework: {
      title: 'El Marco',
      description: 'Profundice en el modelo conductual de cinco dimensiones'
    },
    application: {
      title: 'Aplicación',
      description: 'Ponga en práctica su conocimiento en escenarios reales'
    }
  },
  
  responsibleUse: {
    title: 'Uso Responsable',
    points: [
      'Los perfiles son un punto de datos—combine con entrevistas y experiencia',
      'Las puntuaciones muestran preferencias, no capacidades o limitaciones',
      'El contexto importa—el mismo perfil puede sobresalir o tener dificultades según el puesto',
      'Nunca use perfiles para tomar decisiones de contratación de aprobación/rechazo',
      'Los perfiles no deben usarse para excluir candidatos de grupos protegidos',
      'Los resultados individuales pueden variar—siempre entreviste a los candidatos independientemente de la coincidencia del perfil'
    ]
  },
  
  legalDisclaimer: {
    title: 'Descargo de Responsabilidad Legal y Aviso de No Discriminación',
    points: [
      'Esta evaluación no es una evaluación psicológica, evaluación médica o herramienta de diagnóstico de discapacidad',
      'Los perfiles conductuales nunca deben ser la única base para decisiones de empleo',
      'No use perfiles para excluir candidatos basándose en características que puedan correlacionarse con clases protegidas (raza, color, religión, sexo, origen nacional, edad, discapacidad, información genética)',
      'Las organizaciones deben cumplir con las leyes laborales aplicables, incluida la legislación antidiscriminación',
      'Las adaptaciones razonables pueden afectar cómo los individuos responden a las evaluaciones—consulte con un abogado',
      'Los ejemplos de coincidencia de roles son solo ilustrativos—las determinaciones reales de idoneidad laboral requieren análisis formal de trabajo validado para sus roles específicos',
      'Los empleadores son los únicos responsables de garantizar que su uso de esta herramienta cumpla con todas las leyes laborales federales, estatales y locales aplicables'
    ]
  },
  
  sections: {
    chart: {
      title: 'Comprendiendo el Gráfico',
      subtitle: 'Cómo leer visualizaciones conductuales',
    },
    percentile: {
      title: 'La Escala de Percentil',
      subtitle: 'Qué significan las puntuaciones en contexto',
    },
    traits: {
      title: 'Los Cinco Rasgos Conductuales',
      subtitle: 'Comprendiendo las dimensiones D-C-T-O-S',
    },
    width: {
      title: 'Amplitud del Perfil: Amplio vs Estrecho',
      subtitle: 'Lo que indica la distribución de puntuaciones',
    },
    cognitive: {
      title: 'Evaluación Cognitiva',
      subtitle: 'Habilidad mental y resolución de problemas',
    },
    reasoning: {
      title: 'Estilos de Razonamiento',
      subtitle: 'Cómo las personas abordan la resolución de problemas',
    },
    executionSpeed: {
      title: 'Velocidad de Ejecución: La Brecha D-T',
      subtitle: 'Cómo la relación Drive-Task afecta el ritmo',
    },
    applications: {
      title: 'Aplicaciones Prácticas',
      subtitle: 'Cómo usar perfiles en el mundo real',
    },
    talentDesign: {
      title: 'Diseño de Talentos: Construyendo el Equipo Correcto',
      subtitle: 'La estructura Gas-Pegamento-Freno para composición de equipos',
    }
  },

  chartExplainer: {
    title: 'Comprendiendo el Gráfico',
    subtitle: 'Cada perfil conductual se muestra como un gráfico visual. Aquí está cómo leerlo:',
    traits: {
      dominance: 'Drive',
      extraversion: 'Connection',
      patience: 'Task',
      formality: 'Order',
      objectivity: 'Style'
    },
    colorLegend: {
      title: 'Rasgos Codificados por Color'
    },
    position: {
      title: 'Significado de la Posición del Punto',
      left: {
        label: 'Lado Izquierdo (0-40%)',
        description: 'Menor expresión del rasgo'
      },
      center: {
        label: 'Centro (40-60%)',
        description: 'Expresión moderada y equilibrada'
      },
      right: {
        label: 'Lado Derecho (60-100%)',
        description: 'Mayor expresión del rasgo'
      }
    },
    howToRead: {
      title: 'Cómo leer:',
      description: 'Pasa el cursor sobre cualquier punto para ver la puntuación percentil exacta. La distribución visual muestra de un vistazo si alguien tiene un perfil amplio (especializado) o estrecho (versátil).'
    }
  },

  percentileScale: {
    title: 'La Escala de Percentil',
    subtitle: 'Todas las puntuaciones se muestran como percentiles (0-100%) comparando a alguien con la población general.',
    fiftyPercent: {
      title: '¿Qué Significa 50%?',
      description: '50% es el promedio de la población. La mayoría de las personas puntúa entre 40-60% en cada rasgo. Las puntuaciones siguen una distribución de curva de campana.'
    },
    understanding: {
      title: 'Comprendiendo Rangos de Puntuación'
    },
    ranges: {
      low: {
        label: 'Puntuación Baja',
        description: 'El rasgo es menos prominente - refleja la tendencia conductual opuesta'
      },
      moderate: {
        label: 'Puntuación Moderada',
        description: 'Expresión equilibrada - puede adaptarse entre ambos extremos'
      },
      high: {
        label: 'Puntuación Alta',
        description: 'El rasgo es prominente - fuerte preferencia por este estilo conductual'
      }
    },
    analogy: {
      title: 'Analogía del Mundo Real',
      description: 'Piensa en percentiles como altura: 50% significa altura promedio, 80% significa más alto que el 80% de las personas, 20% significa más alto que solo el 20% de las personas. El mismo principio se aplica a los rasgos conductuales.'
    }
  },

  cognitive: {
    title: 'Descripción General de la Evaluación Cognitiva',
    subtitle: 'La prueba cognitiva mide la capacidad mental y habilidad de resolución de problemas.',
    whatItMeasures: {
      title: 'Qué Mide',
      description: 'La evaluación cognitiva evalúa tres habilidades mentales principales:',
      items: {
        verbal: 'Razonamiento verbal - comprender y analizar información escrita',
        numerical: 'Razonamiento numérico - trabajar con números y datos cuantitativos',
        abstract: 'Razonamiento abstracto - reconocer patrones y resolver problemas nuevos'
      }
    },
    difference: {
      title: 'Cómo Difiere de lo Conductual',
      description: 'Los rasgos conductuales miden CÓMO prefieres trabajar (tu estilo). La capacidad cognitiva mide QUÉ TAN BIEN resuelves problemas complejos (tu capacidad).',
      highlight: 'Diferencia clave: Los rasgos conductuales muestran preferencias y tendencias. Las puntuaciones cognitivas muestran capacidad y velocidad de aprendizaje.'
    },
    usingBoth: {
      title: 'Usando Ambos Juntos para Contratación',
      description: 'Las decisiones de contratación más efectivas combinan ambas evaluaciones:',
      items: {
        behavioral: 'Perfil Conductual → ¿Disfrutarán el rol y encajarán en la cultura?',
        cognitive: 'Puntuación Cognitiva → ¿Pueden manejar la complejidad y aprender rápidamente?',
        combined: 'Juntos → Imagen completa de ajuste, capacidad y potencial'
      }
    },
    format: {
      title: 'Formato de la Prueba',
      description: '50 preguntas, 12 minutos, cubriendo razonamiento verbal, numérico y abstracto. Los resultados muestran el percentil general y desglose por tipo de razonamiento.'
    }
  },

  traits: {
    dominance: {
      letter: 'D',
      name: 'Drive',
      definition: 'Mide cuán directo y asertivo es alguien al alcanzar metas y resultados.',
      high: {
        title: 'Puntuación Alta (60%+)',
        characteristics: 'Directo, decisivo, orientado a resultados, toma el mando, cómodo con conflicto',
        strengths: 'Logra cosas rápidamente, toma decisiones difíciles, impulsa cambios',
        watchouts: 'Puede parecer agresivo, puede ignorar sentimientos, puede apresurar decisiones'
      },
      low: {
        title: 'Puntuación Baja (40%-)',
        characteristics: 'Colaborativo, solidario, busca consenso, evita conflictos, paciente',
        strengths: 'Construye relaciones sólidas, escucha bien, crea armonía',
        watchouts: 'Puede evitar confrontación necesaria, puede ser indeciso, puede ceder demasiado'
      },
      moderate: {
        title: 'Puntuación Moderada (40-60%)',
        characteristics: 'Enfoque equilibrado - asertivo cuando es necesario, colaborativo cuando es apropiado'
      }
    },
    extraversion: {
      letter: 'C',
      name: 'Connection',
      definition: 'Mide cuán extrovertido y social es alguien, y cómo obtiene energía.',
      high: {
        title: 'Puntuación Alta (60%+)',
        characteristics: 'Extrovertido, entusiasta, sociable, energizado por personas, expresivo',
        strengths: 'Construye redes fácilmente, energiza equipos, comunicador persuasivo',
        watchouts: 'Puede dominar conversaciones, puede parecer superficial, puede ignorar introvertidos'
      },
      low: {
        title: 'Puntuación Baja (40%-)',
        characteristics: 'Reservado, reflexivo, enfocado, prefiere conexiones profundas, observador',
        strengths: 'Piensa antes de hablar, escucha profundamente, trabaja bien independientemente',
        watchouts: 'Puede parecer distante, puede ser pasado por alto, puede perder oportunidades de networking'
      },
      moderate: {
        title: 'Puntuación Moderada (40-60%)',
        characteristics: 'Ambivertido - cómodo tanto socialmente como independientemente'
      }
    },
    patience: {
      letter: 'T',
      name: 'Task',
      definition: 'Mide preferencia por estabilidad y consistencia versus cambio y variedad.',
      high: {
        title: 'Puntuación Alta (60%+)',
        characteristics: 'Estable, consistente, confiable, prefiere estabilidad, paciente, metódico',
        strengths: 'Mantiene estándares de calidad, construye sistemas duraderos, presencia calmante',
        watchouts: 'Puede resistir cambios necesarios, puede ser lento para adaptarse, puede evitar innovación'
      },
      low: {
        title: 'Puntuación Baja (40%-)',
        characteristics: 'Dinámico, de ritmo rápido, prospera con cambio, busca variedad, inquieto',
        strengths: 'Se adapta rápidamente, impulsa innovación, multitarea efectivamente',
        watchouts: 'Puede parecer impaciente, puede alterar estabilidad, puede perder detalles importantes'
      },
      moderate: {
        title: 'Puntuación Moderada (40-60%)',
        characteristics: 'Equilibrado - aprecia tanto estabilidad como cambio apropiado'
      }
    },
    formality: {
      letter: 'O',
      name: 'Order',
      definition: 'Mide preferencia por estructura y detalle versus flexibilidad y visión global.',
      high: {
        title: 'Puntuación Alta (60%+)',
        characteristics: 'Estructurado, orientado a detalles, sigue procedimientos, analítico, cauteloso',
        strengths: 'Asegura precisión, mantiene cumplimiento, detecta errores',
        watchouts: 'Puede parecer rígido, puede perderse en detalles, puede ralentizar progreso'
      },
      low: {
        title: 'Puntuación Baja (40%-)',
        characteristics: 'Flexible, innovador, enfocado en visión global, espontáneo, toma riesgos',
        strengths: 'Piensa creativamente, se adapta fácilmente, identifica oportunidades',
        watchouts: 'Puede pasar por alto detalles, puede parecer descuidado, puede omitir procedimientos importantes'
      },
      moderate: {
        title: 'Puntuación Moderada (40-60%)',
        characteristics: 'Equilibrado - estructurado cuando es necesario, flexible cuando es apropiado'
      }
    },
    objectivity: {
      letter: 'S',
      name: 'Style',
      definition: 'Mide estilo de pensamiento - analítico y basado en datos versus intuitivo y enfocado en personas.',
      high: {
        title: 'Puntuación Alta (60%+)',
        characteristics: 'Analítico, basado en datos, lógico, objetivo, enfocado en hechos',
        strengths: 'Toma decisiones racionales, resuelve problemas complejos, aporta objetividad',
        watchouts: 'Puede pasar por alto emociones, puede parecer frío, puede perder factores humanos'
      },
      low: {
        title: 'Puntuación Baja (40%-)',
        characteristics: 'Intuitivo, enfocado en personas, empático, orientado a relaciones, guiado por valores',
        strengths: 'Lee bien a las personas, construye confianza, considera impacto en otros',
        watchouts: 'Puede ser influenciado por emociones, puede pasar por alto datos, puede evitar decisiones difíciles'
      },
      moderate: {
        title: 'Puntuación Moderada (40-60%)',
        characteristics: 'Equilibrado - usa tanto lógica como intuición apropiadamente'
      }
    }
  },

  width: {
    wide: {
      title: 'Perfil Amplio',
      definition: 'Gran diferencia entre puntuaciones más altas y más bajas (ej: 85%-25%)',
      characteristics: [
        'Tendencias conductuales más especializadas',
        'Fortalezas claras y pronunciadas en ciertas áreas',
        'Puede necesitar apoyo o desarrollo en otras áreas',
        'Indicadores fuertes de ajuste a roles',
        'Firma de personalidad más definida'
      ],
      example: 'D=85%, C=75%, T=25%, O=30%, S=60%',
      useCase: 'Los perfiles amplios son más fáciles de emparejar con roles específicos - indicadores claros de dónde sobresalen y dónde pueden tener dificultades'
    },
    narrow: {
      title: 'Perfil Estrecho',
      definition: 'Puntuaciones agrupadas alrededor del medio (ej: todas 45%-55%)',
      characteristics: [
        'Más adaptable y versátil',
        'Puede ajustarse a diferentes situaciones',
        'Puede carecer de fortalezas destacadas',
        'Preferencias conductuales menos predecibles',
        'Puede ajustarse a múltiples roles pero puede no sobresalir en posiciones especializadas'
      ],
      example: 'D=55%, C=48%, T=52%, O=45%, S=50%',
      useCase: 'Los perfiles estrechos son generalistas - adaptables pero pueden necesitar orientación clara sobre expectativas y enfoque de desarrollo'
    },
    interpretation: 'Ni amplio ni estrecho es mejor - depende del rol. Los roles especializados necesitan perfiles amplios con el patrón correcto; los roles generalistas pueden beneficiarse de perfiles más estrechos.'
  },

  roleMatching: {
    title: 'Ejemplos de Emparejamiento de Roles',
    subtitle: 'Ve cómo diferentes perfiles DCTOS emparejan con roles laborales específicos',
    keyTraits: 'Rasgos Conductuales Clave',
    whyItFits: 'Por Qué Este Perfil Encaja',
    roles: {
      salesManager: {
        title: 'Gerente de Ventas',
        pattern: 'Alto D, Alto C, Bajo T',
        traits: 'Asertivo, persuasivo, socialmente confiado, rápido, competitivo',
        fit: 'Impulsa resultados a través de carisma y urgencia. Puede influir en clientes y liderar equipos con confianza. Prospera en entornos competitivos orientados a objetivos.'
      },
      accountant: {
        title: 'Contador',
        pattern: 'Bajo D, Alto O, Alto S',
        traits: 'Orientado a detalles, analítico, metódico, preciso, sistemático',
        fit: 'Asegura precisión y cumplimiento a través de análisis cuidadoso. Cómodo trabajando independientemente con números. Prefiere trabajo estructurado y predecible.'
      },
      customerSupport: {
        title: 'Especialista en Atención al Cliente',
        pattern: 'Bajo D, Alto T, Bajo S',
        traits: 'Paciente, empático, estable, enfocado en personas, evita conflictos',
        fit: 'Proporciona soporte tranquilo y consistente. Naturalmente paciente con clientes frustrados. Valora relaciones sobre datos. Crea armonía y confianza.'
      },
      projectManager: {
        title: 'Gerente de Proyectos',
        pattern: 'Alto D, Alto O',
        traits: 'Decisivo, organizado, impulsa responsabilidad, planifica minuciosamente',
        fit: 'Mantiene proyectos en camino a través de estructura y asertividad. Puede tomar decisiones difíciles mientras mantiene planes detallados. Equilibra personas y procesos.'
      },
      creativeDirector: {
        title: 'Director Creativo',
        pattern: 'Alto C, Bajo T, Bajo O',
        traits: 'Innovador, flexible, intuitivo, energético, enfocado en visión global',
        fit: 'Genera ideas frescas e inspira equipos. Cómodo con ambigüedad y cambio. Piensa creativamente en lugar de analíticamente. Prospera en variedad.'
      },
      dataAnalyst: {
        title: 'Analista de Datos',
        pattern: 'Bajo C, Alto O',
        traits: 'Analítico, enfocado en detalles, independiente, lógico, minucioso',
        fit: 'Sobresale encontrando patrones en datos. Prefiere trabajar solo con números. Toma decisiones basadas en evidencia. Valora precisión sobre velocidad.'
      }
    },
    note: {
      title: 'Importante:',
      description: 'Estos son patrones ideales, no requisitos absolutos. Muchas personas exitosas adaptan su estilo natural para ajustarse a su rol. Úsalos como guías, no como reglas rígidas.'
    }
  },

  applications: {
    readingProfiles: {
      title: 'Cómo Leer un Perfil',
      steps: [
        'Observa el patrón general - ¿qué rasgos son altos/bajos?',
        'Verifica la amplitud - ¿especializado o versátil?',
        'Considera combinaciones de rasgos - ¿cómo interactúan?',
        'Compara con los requisitos del rol'
      ]
    },
    combinations: {
      title: 'Combinaciones de Rasgos Comunes',
      patterns: [
        {
          traits: 'Alto D + Bajo T',
          meaning: 'Urgente/motivado - quiere resultados rápidos y cambios rápidos',
          workplace: 'Excelente para gestión de crisis, transformaciones, ejecución rápida'
        },
        {
          traits: 'Alto C + Alto D',
          meaning: 'Líder carismático - socialmente confiado y asertivo',
          workplace: 'Sobresale en ventas, liderazgo, roles de cara al público'
        },
        {
          traits: 'Bajo D + Alto T',
          meaning: 'Apoyo estable - colaborativo y consistente',
          workplace: 'Ideal para servicio al cliente, operaciones, coordinación de equipo'
        },
        {
          traits: 'Alto O + Alto S',
          meaning: 'Perfeccionista analítico - orientado a detalles y lógico',
          workplace: 'Perfecto para finanzas, control de calidad, cumplimiento'
        },
        {
          traits: 'Bajo O + Bajo S',
          meaning: 'Innovador creativo - flexible e intuitivo',
          workplace: 'Prospera en campos creativos, innovación, desarrollo de personas'
        }
      ]
    },
    personaGap: {
      title: 'Comportamiento Self vs Work',
      definition: 'La diferencia entre cómo alguien es naturalmente (Self) y cómo se adapta en el trabajo (Work)',
      interpretation: 'Brechas grandes sugieren adaptación conductual o enmascaramiento - esto puede ser energizante para algunos, agotador para otros',
      redFlags: 'Brechas muy grandes (>30 puntos percentiles) en múltiples rasgos pueden indicar desajuste de rol o riesgo de agotamiento'
    }
  },

  reasoning: {
    title: 'Pensamiento Deductivo vs Inductivo',
    subtitle: 'Comprendiendo cómo las personas abordan problemas y toman decisiones',
    characteristics: 'Características',
    example: 'Ejemplo en el Trabajo',
    bestFor: 'Más Adecuado Para',
    deductive: {
      title: 'Deductivo (D > C)',
      subtitle: 'D mayor que C',
      definition: 'Comienza con teorías generales, reglas o principios y los aplica a situaciones específicas. Se mueve del panorama general a los detalles.',
      characteristics: [
        'Basado en reglas y estructura primero',
        'Teórico y orientado por hipótesis',
        'Aplica conocimiento existente a nuevas situaciones',
        'Prefiere estructura y métodos establecidos'
      ],
      example: 'Si nuestra política establece X, entonces en esta situación debemos hacer Y',
      bestFor: 'Implementación de políticas, trabajo de cumplimiento, procesos estandarizados, aplicación de marcos probados'
    },
    inductive: {
      title: 'Inductivo (C > D)',
      subtitle: 'C mayor que D',
      definition: 'Comienza con observaciones específicas, datos o casos y construye conclusiones generales. Se mueve de los detalles al panorama general.',
      characteristics: [
        'Orientado por datos y evidencia primero',
        'Empírico y busca patrones',
        'Construye teorías a partir de observaciones',
        'Prefiere exploración y descubrimiento'
      ],
      example: 'Hemos observado X sucediendo repetidamente en múltiples casos, así que podemos concluir Y',
      bestFor: 'Investigación, solución de problemas, innovación, desarrollo de estrategia, encontrar nuevos patrones'
    },
    insight: {
      title: 'Insight Clave',
      description: 'Ningún estilo de razonamiento es inherentemente mejor. Los solucionadores de problemas efectivos usan ambos dependiendo del contexto. Cuando tienes marcos probados, el pensamiento deductivo asegura consistencia. Al enfrentar problemas nuevos o buscar innovación, el pensamiento inductivo revela nuevos patrones.',
      connectionTitle: 'Conexión con Perfiles Conductuales',
      connection: 'Los individuos con alto Style (S) a menudo tienden naturalmente hacia el razonamiento deductivo—aplicando marcos lógicos y reglas. Los individuos con bajo Style (S) tienden hacia el razonamiento inductivo—leyendo patrones en comportamiento humano y contexto. Sin embargo, esto es una tendencia, no una limitación, y las personas pueden desarrollar ambos enfoques.'
    }
  },

  executionSpeed: {
    title: 'La Brecha D-T: Velocidad de Ejecución',
    subtitle: 'La relación entre Drive (D) y Task (T) determina el ritmo preferido de trabajo y toma de decisiones',
    gapLabel: 'Brecha',
    fast: {
      title: 'D > T (Ejecución Rápida)',
      subtitle: 'Mayor Urgencia',
      definition: 'Cuando Drive supera Task, la persona prefiere acción rápida y decisiva. Cuanto mayor es la brecha, más urgente es el estilo de ejecución.',
      characteristics: [
        'Toma decisiones rápidas',
        'Impaciente con retrasos o procesos lentos',
        'Orientado a la acción, quiere resultados ahora',
        'Impulsa cambios y transformaciones rápidas',
        'Puede parecer apresurado o insistente para otros'
      ],
      example: '"Decidamos ahora y solucionemos problemas sobre la marcha"',
      bestFor: 'Gestión de crisis, cierre de ventas, reestructuraciones, entornos de startups',
      watchouts: 'Puede comprometer la calidad, pasar por alto detalles importantes, crear estrés para colegas de ritmo más lento'
    },
    deliberate: {
      title: 'T > D (Ritmo Deliberado)',
      subtitle: 'Estable y Metódico',
      definition: 'Cuando Task supera Drive, la persona prefiere un enfoque medido y exhaustivo. Cuanto mayor es la brecha, más metódico es el estilo.',
      characteristics: [
        'Se toma tiempo para considerar opciones',
        'Valora la minuciosidad sobre la velocidad',
        'Mantiene producción constante y consistente',
        'Prefiere procesos probados',
        'Puede parecer lento para colegas urgentes'
      ],
      example: '"Pensemos esto bien antes de comprometernos"',
      bestFor: 'Aseguramiento de calidad, operaciones, atención al cliente, cumplimiento, investigación',
      watchouts: 'Puede perder plazos urgentes, lento para responder en crisis, podría frustrar a compañeros de ritmo rápido'
    },
    balanced: {
      title: 'D ≈ T (Ritmo Equilibrado)',
      description: 'Cuando Drive y Task son similares, la persona puede adaptar su ritmo a la situación - urgente cuando es necesario, paciente cuando es apropiado.'
    },
    insight: {
      title: 'Insight Clave',
      description: 'Ni la ejecución rápida ni la lenta es inherentemente mejor. El ritmo correcto depende del rol y el contexto. Ajuste las expectativas de velocidad de ejecución a los requisitos del puesto.',
      howToUse: 'Cómo aplicar esto:',
      howToUseDescription: 'Los roles con alta brecha D-T (ventas, gestión de crisis) necesitan perfiles D > T. Los roles de baja urgencia (soporte, QA) se benefician de perfiles T > D. Los roles equilibrados funcionan con ambos.'
    }
  },

  talentDesign: {
    title: 'Diseño de Talentos: Construyendo el Equipo Correcto',
    subtitle: 'Cómo ensamblar equipos de alto rendimiento según la fase de la empresa',
    introduction: 'Cada equipo necesita tres fuerzas trabajando en armonía: Gas para impulsar el crecimiento, Pegamento para unir a las personas, y Freno para asegurar la excelencia. El equilibrio correcto depende de la fase de tu empresa.',
    forcesTitle: 'Las Tres Fuerzas',
    forces: {
      gas: {
        title: '⛽ GAS',
        trait: 'D (Drive)',
        description: 'Impulsa el crecimiento y dirige el impulso hacia adelante. Gas empuja límites, toma riesgos calculados y acelera el progreso.',
        characteristics: [
          'Impulsa resultados y presiona por acción',
          'Toma iniciativa sin esperar permiso',
          'Desafía el status quo y rompe barreras',
          'Cómodo con incertidumbre y cambio rápido'
        ]
      },
      glue: {
        title: '🧲 PEGAMENTO',
        trait: 'C (Connection)',
        description: 'Une personas y construye cultura. Pegamento crea cohesión, facilita comunicación y mantiene el espíritu de equipo.',
        characteristics: [
          'Construye relaciones entre equipos y departamentos',
          'Facilita comunicación y resuelve conflictos',
          'Crea sentido de pertenencia y propósito compartido',
          'Energiza a otros y mantiene la moral'
        ]
      },
      brake: {
        title: '🛑 FRENO',
        trait: 'O (Order)',
        description: 'Asegura excelencia y mantiene calidad. Freno previene errores costosos, refuerza estándares y protege lo que funciona.',
        characteristics: [
          'Atrapa errores antes de que se conviertan en problemas',
          'Mantiene sistemas, procesos y documentación',
          'Asegura cumplimiento y estándares de calidad',
          'Proporciona estabilidad y previsibilidad'
        ]
      }
    },
    phasesTitle: 'Composición de Equipo Basada en Fase',
    phases: {
      startup: {
        title: '🚀 Fase Startup',
        primaryNeed: 'Velocidad & Crecimiento',
        focus: 'Mucho GAS (Alto D)',
        whatToLookFor: 'Contrata impulsores que empujan límites, toleran ambigüedad, se mueven rápido y no temen romper cosas. Demasiado freno te frenará antes de encontrar product-market fit.'
      },
      scaling: {
        title: '📈 Fase de Crecimiento/Escalado',
        primaryNeed: 'Cultura & Alineación',
        focus: 'Agrega PEGAMENTO (Alto C)',
        whatToLookFor: 'Trae conectores que construyen puentes entre equipos en crecimiento, mantienen la cultura mientras escalas, y mantienen a todos alineados con la misión. Sin pegamento, los equipos se fragmentan y se forman silos.'
      },
      mature: {
        title: '🏢 Fase Madura/Optimización',
        primaryNeed: 'Excelencia & Eficiencia',
        focus: 'Fortalece el FRENO (Alto O)',
        whatToLookFor: 'Recluta guardianes de calidad que refuerzan estándares, previenen errores costosos en sistemas establecidos, y optimizan lo que ya funciona. El freno protege tus ganancias y asegura excelencia sostenible.'
      }
    },
    warningsTitle: 'Advertencias de Equilibrio',
    warnings: [
      {
        condition: 'Todo GAS, sin FRENO',
        consequence: 'Caos, agotamiento, problemas de calidad, errores costosos que podrían haberse prevenido'
      },
      {
        condition: 'Todo PEGAMENTO, sin GAS',
        consequence: 'Muchas reuniones y charlas, pero sin ejecución ni movimiento hacia adelante'
      },
      {
        condition: 'Todo FRENO, sin GAS',
        consequence: 'Parálisis por análisis, oportunidades perdidas, muerte lenta por sobre-optimización'
      }
    ],
    practicalApplication: {
      title: '💡 Conclusión Clave',
      content: 'No existe una composición de equipo "mejor" universal. Una startup necesita combustible diferente que una empresa Fortune 500. Audita tu equipo actual: ¿Qué fuerza te falta? ¿Qué fuerza tienes en exceso para tu fase actual? Luego contrata en consecuencia.'
    }
  }
};
